import { Check, Info } from 'lucide-react';

import { Button } from '@/components/ui/button';

import { Tooltip } from '@/shared/components/tooltip';
import { COPY } from '@/shared/constants/copies';

import { cn } from '@/lib/utils';

import { PlanFeatures } from '../../types';

const PlanCard = ({
	plan,
	features,
	price,
	buttonText,
	onButtonClick,
	buttonDisabled = false,
	isRaised = false,
	hideButton = false,
	workspaceName,
	className,
	primaryStyledButton = isRaised,
	// Pricing is per workspace, never per seat — every caller passes this, but the
	// default must not imply seat-based billing if one ever forgets.
	priceSubtext = COPY.pricing.perMonth,
	hidePlanTitle = false,
	priceOverride,
	featuresMarginClass,
	compact = false,
}: {
	plan: string;
	features: PlanFeatures;
	price: string;
	buttonText: string;
	onButtonClick: () => void;
	buttonDisabled?: boolean;
	/**
	 * Renders this card lifted out of the row — border, shadow and a slight scale.
	 *
	 * Currently passed by nobody: the raised treatment is switched off on every
	 * pricing surface. Kept because the styling is intact and turning it back on is
	 * one prop; if it stays unused, delete it rather than letting it rot.
	 *
	 * Note it also defaults `primaryStyledButton`, so a caller that wants the filled
	 * CTA without the lift must pass `primaryStyledButton` explicitly — every caller
	 * does today.
	 */
	isRaised?: boolean;
	hideButton?: boolean;
	workspaceName?: string;
	className?: string;
	primaryStyledButton?: boolean;
	priceSubtext?: string | null;
	hidePlanTitle?: boolean;
	priceOverride?: string;
	featuresMarginClass?: string;
	compact?: boolean;
}) => {
	return (
		<div
			className={cn(
				// Horizontal padding is tighter than vertical: with four cards in one row
				// every 8px of side padding comes straight out of the label width, which
				// is what pushes feature lines onto a second line.
				'flex w-full min-w-0 flex-1 flex-col p-8 md:px-5 lg:px-6',
				compact && 'p-6 md:px-4 lg:px-5',
				isRaised &&
					'md:relative md:z-10 md:scale-[1.04] md:rounded-lg md:border md:border-slate-200 md:bg-white md:shadow-[0_8px_30px_rgba(0,0,0,0.08)]',
				className
			)}
		>
			<div
				className={cn(
					'mb-6 mt-0 flex flex-col gap-3 md:mb-0 md:min-h-[140px] lg:mt-4',
					compact && 'mb-5 gap-2 md:min-h-[96px] lg:mt-2'
				)}
			>
				{!hidePlanTitle && (
					<div className="flex items-center justify-between gap-2">
						<h2 className={cn('text-lg font-medium text-slate-600', compact && 'text-base')}>
							{plan}
						</h2>
						{workspaceName && (
							<span className="rounded bg-slate-100 px-2 py-1 text-xs text-slate-400">
								{workspaceName}
							</span>
						)}
					</div>
				)}
				<div className={cn('flex items-baseline gap-2', hidePlanTitle && 'mt-10')}>
					{(priceOverride || price) && (
						<span
							className={cn(
								'text-[34px] font-semibold tracking-tight text-primary md:text-[44px]',
								compact && 'text-[30px] md:text-[36px]'
							)}
						>
							{priceOverride || price}
						</span>
					)}
					{price !== '0' && price !== '$0' && priceSubtext && (
						<span
							className={cn(
								'whitespace-nowrap text-sm font-medium leading-5 text-slate-500',
								compact && 'text-xs'
							)}
						>
							{priceSubtext}
						</span>
					)}
				</div>
			</div>

			{!hideButton && (
				<div className="mb-4 mt-2 flex flex-col gap-2">
					<Button
						className={cn(
							'h-11 w-full text-[15px] transition-all hover:translate-y-0.5',
							compact && 'h-10 text-sm',
							buttonDisabled
								? ''
								: primaryStyledButton
									? 'shadow-[0px_8px_10px_0px_#3C424C3D]'
									: 'bg-[#F5F5F5] shadow-[0px_8px_10px_0px_#bdc1c82e]'
						)}
						variant={primaryStyledButton ? 'default' : 'outline'}
						onClick={onButtonClick}
						disabled={buttonDisabled}
					>
						{buttonText}
					</Button>
				</div>
			)}

			<div
				className={cn(
					featuresMarginClass || (compact ? 'mt-4' : 'mt-6'),
					hideButton && !featuresMarginClass && (compact ? 'mt-[80px]' : 'mt-[92px]')
				)}
			>
				<FeatureList
					features={features}
					compact={compact}
				/>
			</div>
		</div>
	);
};

export default PlanCard;

const FeatureText = ({ text, href, compact }: { text: string; href?: string; compact: boolean }) =>
	href ? (
		<a
			href={href}
			target="_blank"
			rel="noreferrer"
			className={cn(
				'text-[15px] text-secondary underline decoration-dashed decoration-secondary/40 underline-offset-[3px] transition-colors hover:text-primary hover:decoration-secondary/70 md:text-base',
				compact && 'text-sm md:text-[15px]'
			)}
		>
			{text}
		</a>
	) : (
		<span className={cn('text-[15px] text-secondary md:text-base', compact && 'text-sm md:text-[15px]')}>
			{text}
		</span>
	);

const FeatureList = ({ features, compact }: { features: PlanFeatures; compact: boolean }) => (
	<ul className={cn('space-y-3 text-base md:mb-8', compact && 'space-y-2.5')}>
		{features.map((feature, index) => {
			const isFeatureObject = typeof feature === 'object';
			const featureText = isFeatureObject ? feature.text : feature;
			const tooltip = isFeatureObject ? feature.tooltip : null;
			const withoutInfoIcon = isFeatureObject ? feature.withoutInfoIcon : false;
			const href = isFeatureObject ? feature.href : undefined;
			const tooltipProps = tooltip == null ? {} : { text: tooltip };

			return (
				<li
					key={index}
					className="flex items-start"
				>
					<Check className="mr-3 size-4 flex-shrink-0 translate-y-1 text-secondary" />
					<div className="flex flex-1 items-start gap-2">
						{withoutInfoIcon && tooltip ? (
							<Tooltip
								{...tooltipProps}
								className="!max-w-[270px]"
							>
								<FeatureText
									text={featureText}
									href={href}
									compact={compact}
								/>
							</Tooltip>
						) : (
							<FeatureText
								text={featureText}
								href={href}
								compact={compact}
							/>
						)}

						{tooltip && !withoutInfoIcon && (
							<Tooltip
								{...tooltipProps}
								className="!max-w-[270px]"
							>
								{/* `flex-shrink-0` is load-bearing: without it a label that wraps to two
								    lines squashes the icon into an ellipse. */}
								<Info className="size-4 flex-shrink-0 translate-y-px cursor-default text-secondary hover:text-slate-600" />
							</Tooltip>
						)}
					</div>
				</li>
			);
		})}
	</ul>
);
